import React from 'react';
import useRipple from 'use-ripple-hook';
import { withTheme } from '@emotion/react';
import styled from '@emotion/styled';
import { Icon } from '@components/common/generic-icon';
import { Button, ButtonSize, ButtonColor } from '@components/material-ui';
import { Amount, Price, TripTypes } from '@model/common';
import { Theme, ThemeProps } from '@theme/base';
import { useI18NextContext } from '@components/hooks';
import { TourLink } from '@components/common/next-link/tour-link';
import BaseCard from '../base/BaseCard';

export interface TourExtensionProps {
  title: string;
  length: number;
  price: Price;
  theme: Theme;
  imageUrl: string;
  testId?: string;
  path: string;
  tripType: TripTypes;
}

const Container = styled.div({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  justifyContent: 'space-between'
});

const Title = styled.h4(({ theme }: ThemeProps) => ({
  ...(theme.custom.typography.h4 as any),
  margin: 0,
  marginBottom: 10,
  color: theme.custom.colors.group4.base
}));

const ExtensionCount = styled.div(({ theme }: ThemeProps) => ({
  ...(theme.custom.typography.paragraph as any),
  color: theme.custom.colors.group4.light,
  marginBottom: 10
}));

const FromPriceContainer = styled.div({
  marginBottom: 20,
  display: 'flex',
  alignItems: 'flex-end'
});

const FromPrice = styled.h4(({ theme }: ThemeProps) => ({
  ...(theme.custom.typography.h4 as any),
  margin: 0,
  marginRight: theme.custom.spacing.xSmall
}));

const FromPriceTitle = styled.span(({ theme }: ThemeProps) => ({
  paddingBottom: 5,
  marginRight: theme.custom.spacing.xSmall,
  ...(theme.custom.typography.labels.attribute as any)
}));

const nightsIconStyles = (theme: Theme) => ({
  fontSize: '1.5rem',
  marginRight: theme.custom.spacing.xSmall,
  color: theme.custom.colors.group4.base
});

export const TourExtensionComponent = ({
  title,
  length,
  price,
  theme,
  testId,
  imageUrl,
  path,
  tripType
}: TourExtensionProps) => {
  const amount: Amount = new Amount(price.amount, price.currency.code);
  const t: any = useI18NextContext();
  const [ripple, event] = useRipple({ color: `${theme.custom.colors.group10.dark}80` });

  return (
    <div ref={ripple}>
      <BaseCard
        testId={testId}
        disableRipple
        renderBody={() => (
          <Container>
            <Title>{title}</Title>
            <ExtensionCount>
              <Icon name={'mhi-nights'} css={nightsIconStyles(theme!)} />
              {length} {t('tour-extension__extension-count--label')}
            </ExtensionCount>
            <FromPriceContainer>
              {price.amount > 0 && (
                <>
                  <FromPriceTitle>{t('common__label--from')}</FromPriceTitle>
                  <FromPrice>{amount.format()}</FromPrice>
                  <FromPriceTitle>{t('common__label--per-person-short')}</FromPriceTitle>
                </>
              )}
            </FromPriceContainer>
            <TourLink path={path} tripType={tripType} onMouseDown={event}>
              <Button
                label={t('tour-extension__extension-cta')}
                color={ButtonColor.SECONDARY}
                size={ButtonSize.SMALL}
              />
            </TourLink>
          </Container>
        )}
        image={imageUrl}
      />
    </div>
  );
};

export const TourExtension = withTheme(TourExtensionComponent);
